<template>
    <div role="alert" class="notification" :class="[`notification-${theme}`, { 'pr-10': !hideDismiss }]">
        <i v-if="badgeIcon" class="notification-badge" :class="badgeIcon" aria-hidden="true"></i>
        <slot name="content">
            <span v-if="errorText" class="notification-text grow fs-mask">{{ errorText }}</span>
            <MarkdownText v-else-if="text" class="notification-text grow" :text="String(text)" />
        </slot>
        <button v-if="!hideDismiss" type="button" :title="t('common.dismiss')" class="bi bi-x text-2xl notification-dismiss" @click="handleDismiss"></button>
    </div>
</template>

<script lang="ts">
export type BaseNotificationTheme = "primary" | "error" | "warning" | "info" | "success";
</script>

<script setup lang="ts">
import MarkdownText from "~vue/MarkdownText.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const {
    theme = "primary",
    text = "",
    hideDismiss = false,
} = defineProps<{
    theme?: BaseNotificationTheme;
    /** An Error (e.g. useFetch's error ref) renders its message as plain text; strings render as markdown. */
    text?: string | Error;
    hideDismiss?: boolean;
}>();

// Absorb the common failure mode instead of pushing it to 90+ call sites:
// binding a fetch error object used to crash marked.parse mid-render, and
// exception text was never authored as markdown anyway.
const errorText = computed(() => (text instanceof Error ? text.message || String(text) : null));

const badgeIcon = computed(() => {
    switch (theme) {
        case "warning":
            return "bi bi-exclamation-lg";
        case "success":
            return "bi bi-check-lg";
        default:
            return null;
    }
});

// emits the dismiss event, which needs to be listened for and handled by the parent
const handleDismiss = () => {
    emit("dismiss");
};

const emit = defineEmits<{
    dismiss: [];
}>();
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .notification {
        @apply relative rounded-xl py-3 px-6 border-2 gap-4;
    }

    .notification-text {
        @apply body-small;
    }

    .notification-success .notification-text {
        @apply details;
    }

    /* Whole selector inside :global() because the scoped transform drops
       everything after a leading :global(...) part. */
    :global(html[data-theme="tonal"] .notification) {
        @apply rounded-md;
    }

    :global(html[data-theme="tonal"] .notification .notification-text) {
        @apply scaffold-body-small;
    }

    .notification-primary {
        @apply border-notification-primary-border bg-notification-primary-bg text-notification-primary-text;
    }

    .notification-error {
        @apply border-notification-error-border bg-notification-error-bg text-notification-error-text;
    }

    .notification-info {
        @apply border-notification-info-border bg-notification-info-bg text-notification-info-text;
    }

    .notification-success {
        @apply flex items-center border border-notification-success-border bg-notification-success-bg text-notification-success-text rounded-lgr py-3 px-4 gap-2.5;
    }

    .notification-warning {
        @apply flex items-center border border-notification-warning-border bg-notification-warning-bg text-notification-warning-text rounded-lgr py-3 px-4 gap-2.5;
    }

    .notification-dismiss {
        @apply absolute top-2 right-2;
    }

    .notification-warning .notification-dismiss,
    .notification-success .notification-dismiss {
        @apply top-1/2 right-3 -translate-y-1/2;
    }

    .notification-badge {
        @apply shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-base/none;
    }

    .notification-warning .notification-badge {
        @apply bg-notification-badge-warning;
    }

    .notification-success .notification-badge {
        @apply bg-notification-badge-success;
    }
}
</style>
